import React, { useState } from 'react';
import {
  PiMagnifyingGlass,
  PiFunnelSimple,
  PiStorefront,
  PiEye,
  PiArrowClockwise,
  PiWarningCircle,
  PiProhibit,
  PiCheck,
  PiTrash,
  PiBasket,
} from 'react-icons/pi';
import { PageSkeleton } from '../DashboardLoader';
import { useAdminGroceryShops } from '../../../hooks/useAdminGroceryShops';

const iconBtn =
  'inline-flex items-center justify-center rounded-xl bg-stone-100 text-stone-600 hover:bg-stone-200 hover:text-stone-800 transition-colors';

const AdminGroceryShops = () => {
  const {
    groceryShops,
    loading,
    error,
    suspendGroceryShopById,
    activateGroceryShopById,
    deleteGroceryShopById,
    refetch,
  } = useAdminGroceryShops();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [actionLoading, setActionLoading] = useState({});
  const [imgFailed, setImgFailed] = useState({});

  const handleSuspend = async (id) => {
    if (!window.confirm('Suspendre ce grocery shop ? Il ne sera plus visible des clients.')) return;
    setActionLoading((s) => ({ ...s, [id]: true }));
    const result = await suspendGroceryShopById(id);
    setActionLoading((s) => ({ ...s, [id]: false }));
    if (!result.success) alert(`Erreur: ${result.error}`);
  };

  const handleActivate = async (id) => {
    setActionLoading((s) => ({ ...s, [id]: true }));
    const result = await activateGroceryShopById(id);
    setActionLoading((s) => ({ ...s, [id]: false }));
    if (!result.success) alert(`Erreur: ${result.error}`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce grocery shop ? Cette action est irréversible.')) return;
    setActionLoading((s) => ({ ...s, [id]: true }));
    const result = await deleteGroceryShopById(id);
    setActionLoading((s) => ({ ...s, [id]: false }));
    if (!result.success) alert(`Erreur: ${result.error}`);
  };

  const filteredShops = groceryShops.filter((shop) => {
    const matchesSearch =
      shop.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(shop.restaurant_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterStatus === 'all' ||
      (filterStatus === 'active' && shop.is_active) ||
      (filterStatus === 'inactive' && !shop.is_active);
    return matchesSearch && matchesFilter;
  });

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <PiWarningCircle className="w-5 h-5 text-red-500 mr-2 shrink-0" />
            <h2 className="text-lg font-semibold text-red-800">Erreur de chargement</h2>
          </div>
          <p className="text-red-600 mt-2">{error}</p>
          <button
            onClick={refetch}
            className="mt-3 inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <PiArrowClockwise className="w-4 h-4 mr-2" />
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (loading) return <PageSkeleton />;

  return (
    <div className="p-4">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Grocery shop</h1>
        <p className="text-gray-900 text-sm mt-1">Gérez les magasins d'épicerie de la plateforme</p>
      </div>

      <div className="bg-white p-4 rounded-xl mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <PiMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 w-5 h-5 pointer-events-none" />
              <input
                type="text"
                placeholder="Rechercher un magasin ou un restaurant..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[rgb(5,70,37)]/25 focus:border-[rgb(5,70,37)]"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 sm:min-w-[200px]">
            <span className={`${iconBtn} w-10 h-10 shrink-0`}>
              <PiFunnelSimple className="w-5 h-5" />
            </span>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="flex-1 px-3 py-2.5 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[rgb(5,70,37)]/25"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredShops.map((shop) => {
          const isLoading = actionLoading[shop.id];
          const showImage = shop.image_url && !imgFailed[shop.id];

          return (
            <div key={shop.id} className="bg-white rounded-2xl overflow-hidden transition-shadow ring-1 ring-stone-100">
              <div className="h-40 bg-gradient-to-br from-stone-100 to-stone-200 relative overflow-hidden">
                {showImage ? (
                  <img
                    src={shop.image_url}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={() => setImgFailed((f) => ({ ...f, [shop.id]: true }))}
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-stone-400 gap-2">
                    <PiBasket className="w-14 h-14 opacity-60" />
                    <span className="text-xs font-medium">Aucune image</span>
                  </div>
                )}
                <div className="absolute top-3 right-3">
                  <span
                    className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                      shop.is_active
                        ? 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200/80'
                        : 'bg-stone-100 text-stone-700 ring-1 ring-stone-200/80'
                    }`}
                  >
                    {shop.is_active ? 'Actif' : 'Inactif'}
                  </span>
                </div>
              </div>

              <div className="p-5">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{shop.name}</h3>
                  <p className="text-sm text-gray-900 flex items-center gap-1.5">
                    <PiStorefront className="w-4 h-4 text-stone-400 shrink-0" />
                    {shop.restaurant_name || 'Aucun restaurant lié'}
                  </p>
                  {shop.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{shop.description}</p>
                  )}
                </div>

                <div className="mb-4">
                  <div className="text-center rounded-xl bg-stone-50 py-3">
                    <p className="text-2xl font-semibold text-gray-900">{shop.products_count ?? 0}</p>
                    <p className="text-xs text-gray-500">Produits au catalogue</p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      title="Voir la fiche publique"
                      onClick={() => window.open(`/grocery-shop/${shop.id}`, '_blank', 'noopener,noreferrer')}
                      className={`${iconBtn} w-10 h-10`}
                    >
                      <PiEye className="w-5 h-5" />
                    </button>
                    {shop.is_active ? (
                      <button
                        type="button"
                        onClick={() => handleSuspend(shop.id)}
                        disabled={isLoading}
                        className={`${iconBtn} w-10 h-10 disabled:opacity-50`}
                        title="Suspendre"
                      >
                        {isLoading ? <PiArrowClockwise className="w-5 h-5 animate-spin" /> : <PiProhibit className="w-5 h-5" />}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleActivate(shop.id)}
                        disabled={isLoading}
                        className={`${iconBtn} w-10 h-10 disabled:opacity-50`}
                        title="Activer"
                      >
                        {isLoading ? <PiArrowClockwise className="w-5 h-5 animate-spin" /> : <PiCheck className="w-5 h-5" />}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDelete(shop.id)}
                      disabled={isLoading}
                      className={`${iconBtn} w-10 h-10 hover:bg-rose-100 hover:text-rose-700 disabled:opacity-50`}
                      title="Supprimer"
                    >
                      {isLoading ? <PiArrowClockwise className="w-5 h-5 animate-spin" /> : <PiTrash className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredShops.length === 0 && !loading && (
        <div className="text-center py-12">
          <PiBasket className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-gray-500">Aucun grocery shop trouvé</p>
        </div>
      )}
    </div>
  );
};

export default AdminGroceryShops;
